import { AGENTIC_LOGIC_SURFACE_LABEL } from "@intelligems/ig-types/dtos/agentic-logic/surface";
import type {
  RuleGraphConditionSubject,
  RuleGraphNode,
} from "@intelligems/ig-types/dtos/agentic-logic/rule-graph";
import { RuleGraphConditionSubjectSchema } from "@intelligems/ig-types/dtos/agentic-logic/rule-graph";
import { IgChip } from "@intelligems/ig-theme/IgChip";
import { IgMonoText } from "@intelligems/ig-theme/IgMonoText";
import { IgTypography } from "@intelligems/ig-theme/IgTypography";
import { Stack } from "@mui/material";
import { formatScenarioDelivery } from "./scenario-context";
import type { RuleGraphNodeOverlay } from "./types";
import { IgIconButton } from "@intelligems/ig-theme/IgIconButton";

/** What the panel calls each condition subject, in the merchant's words. */
const CONDITION_SUBJECT_LABELS: Record<RuleGraphConditionSubject, string> = {
  cart: "cart",
  delivery: "delivery",
  shipping: "shipping",
  customer: "shopper",
  page: "page",
  order: "order",
  time: "time",
};

/**
 * The click-to-inspect panel beside the canvas: the full summary and the meta
 * aggregates the card elides (sampleItems and any unknown keys). This is also
 * where later overlay tickets add per-node drill-in (attempt instances,
 * branchFired telemetry) without touching the layout.
 */
export const RuleGraphNodeDetailsPanel = ({
  id,
  node,
  overlay,
  onClose,
}: {
  /** Referenced by the opening node's `aria-controls`. */
  id: string;
  node: RuleGraphNode;
  overlay: RuleGraphNodeOverlay | undefined;
  /** The X button. The canvas owns Escape and focus restoration. */
  onClose: () => void;
}) => {
  const sampleItems = Array.isArray(node.meta?.sampleItems)
    ? node.meta.sampleItems.filter((item) => typeof item === "string")
    : [];
  // A branch's condition subject gets a labeled row of its own; a value the
  // panel does not know stays in the raw rows so nothing is hidden.
  const conditionSubject =
    node.kind === "branch"
      ? RuleGraphConditionSubjectSchema.safeParse(node.meta?.conditionSubject)
      : undefined;
  const remainingMeta = Object.entries(node.meta ?? {}).filter(
    ([key]) =>
      key !== "sampleItems" &&
      !(key === "conditionSubject" && conditionSubject?.success === true)
  );

  return (
    <Stack
      aria-label={`${node.label} details`}
      data-testid="rule-graph-node-details"
      id={id}
      role="region"
      spacing={1.5}
      sx={{
        borderLeft: "1px solid",
        borderLeftColor: "divider",
        overflowY: "auto",
        px: 2,
        py: 1.5,
        width: 320,
      }}
    >
      <Stack direction="row" spacing={1} alignItems="center">
        <IgTypography variant="body2" fontWeight={700} sx={{ flex: 1 }}>
          {node.label}
        </IgTypography>
        <IgChip label={node.kind} size="small" variant="outlined" />
        <IgIconButton
          aria-label="Close details"
          iconSize={16}
          iconType="close"
          onClick={onClose}
          size="small"
          sx={{ flexShrink: 0, mr: -1 }}
        />
      </Stack>

      {node.summary ? (
        <IgTypography variant="body2" sx={{ color: "text.secondary" }}>
          {node.summary}
        </IgTypography>
      ) : null}

      {node.branchId ? (
        <Stack spacing={0.5}>
          <IgTypography variant="caption" sx={{ color: "text.secondary" }}>
            Part of branch
          </IgTypography>
          <IgMonoText value={node.branchId} />
        </Stack>
      ) : null}

      {conditionSubject?.success ? (
        <Stack spacing={0.5}>
          <IgTypography variant="caption" sx={{ color: "text.secondary" }}>
            Condition on
          </IgTypography>
          <IgTypography
            variant="body2"
            data-testid="rule-graph-node-condition-subject"
          >
            {CONDITION_SUBJECT_LABELS[conditionSubject.data]}
          </IgTypography>
        </Stack>
      ) : null}

      {remainingMeta.length > 0 ? (
        <Stack spacing={0.5}>
          <IgTypography variant="caption" sx={{ color: "text.secondary" }}>
            Details
          </IgTypography>
          {remainingMeta.map(([key, value]) => (
            <Stack key={key} direction="row" spacing={1} alignItems="baseline">
              <IgMonoText value={key} sx={{ color: "text.secondary" }} />
              <IgTypography variant="body2" sx={{ wordBreak: "break-word" }}>
                {typeof value === "string" ? value : JSON.stringify(value)}
              </IgTypography>
            </Stack>
          ))}
        </Stack>
      ) : null}

      {sampleItems.length > 0 ? (
        <Stack spacing={0.5}>
          <IgTypography variant="caption" sx={{ color: "text.secondary" }}>
            Sample items
          </IgTypography>
          {sampleItems.map((item) => (
            <IgMonoText key={item} value={item} noWrap title={item} />
          ))}
        </Stack>
      ) : null}

      {overlay?.attempts && overlay.attempts.length > 0 ? (
        <Stack spacing={0.5}>
          <IgTypography variant="caption" sx={{ color: "text.secondary" }}>
            Attempts this run
          </IgTypography>
          {overlay.attempts.map((attempt, index) => (
            <Stack
              key={`${attempt.base}:${attempt.instance ?? index}`}
              direction="row"
              spacing={1}
              alignItems="baseline"
            >
              <IgMonoText
                value={attempt.instance ?? attempt.base}
                sx={{ color: "text.secondary" }}
              />
              <IgTypography variant="body2">
                {attempt.outcome} · {attempt.contributed} contributed
              </IgTypography>
            </Stack>
          ))}
        </Stack>
      ) : null}

      {overlay?.result?.scenarioContext ? (
        <Stack spacing={0.5} data-testid="rule-graph-node-scenario">
          <IgTypography variant="caption" sx={{ color: "text.secondary" }}>
            Scenario
          </IgTypography>
          <ScenarioRow
            label="Surface"
            value={
              AGENTIC_LOGIC_SURFACE_LABEL[
                overlay.result.scenarioContext.surface
              ]
            }
          />
          {overlay.result.scenarioContext.delivery ? (
            <ScenarioRow
              label="Delivery"
              value={formatScenarioDelivery(
                overlay.result.scenarioContext.delivery
              )}
            />
          ) : null}
          {overlay.result.scenarioContext.customer ? (
            <ScenarioRow
              label="Orders placed"
              value={String(
                overlay.result.scenarioContext.customer.ordersCount
              )}
            />
          ) : null}
        </Stack>
      ) : null}

      {overlay?.result && !overlay.result.empty ? (
        <Stack spacing={0.5}>
          <IgTypography variant="caption" sx={{ color: "text.secondary" }}>
            What the shopper sees
          </IgTypography>
          {overlay.result.products.map((product) => (
            <Stack
              key={product.id}
              direction="row"
              spacing={1}
              alignItems="baseline"
            >
              <IgTypography variant="body2" sx={{ flex: 1, minWidth: 0 }}>
                {product.title}
                {product.variantTitle ? (
                  <IgTypography
                    component="span"
                    variant="body2"
                    sx={{ color: "text.secondary" }}
                  >
                    {` · ${product.variantTitle}`}
                  </IgTypography>
                ) : null}
              </IgTypography>
              <IgTypography variant="body2" fontWeight={600}>
                {product.price}
              </IgTypography>
            </Stack>
          ))}
          <IgTypography variant="caption" sx={{ color: "text.secondary" }}>
            {overlay.result.explanation}
          </IgTypography>
        </Stack>
      ) : null}

      {overlay?.detail ?? null}
    </Stack>
  );
};

const ScenarioRow = ({ label, value }: { label: string; value: string }) => (
  <Stack direction="row" spacing={1} alignItems="baseline">
    <IgTypography
      variant="body2"
      sx={{ color: "text.secondary", flexShrink: 0 }}
    >
      {label}
    </IgTypography>
    <IgTypography variant="body2" sx={{ wordBreak: "break-word" }}>
      {value}
    </IgTypography>
  </Stack>
);
